//-----------------------------------------------
// Generic Autogen Helper
// Provides pattern-based value generation for any field.
// Used by TotalField for generic autogen and by Identifier for ID-specific autogen.
//-----------------------------------------------
import { collectScopedInputValues } from "./fieldCollection.mjs";

export default class Autogen {

	// Maximum length of any single field value substituted into a pattern.
	// Generous for real titles/names, but caps rich-text fields (e.g. styletext)
	// that would otherwise blow the generated ID past the filesystem's ~255-char
	// filename limit. Special tokens (oid, uid, timestamp, ...) are added after
	// collection, so uniqueness suffixes are never truncated. Mirrors
	// AutogenService::MAX_FIELD_LENGTH (PHP).
	static MAX_FIELD_LENGTH = 100;

	constructor(field) {
		this.field = field;
		this.pattern = field.settings.autogen;
	}

	/**
	 * Parse the autogen pattern and return the referenced field names
	 * (excluding reserved names).
	 */
	getFieldNames() {
		const reservedNames = [
			"now", "timestamp", "uuid", "uid", "id", "oid",
			"currentyear", "currentyear2", "currentmonth", "currentday"
		];
		return (this.pattern.match(/\${(.*?)}/g) || [])
			.map(v => v.slice(2, -1))
			.filter(name => !reservedNames.includes(name)
				&& !name.startsWith('oid-')
				&& !name.startsWith('uid-'));
	}

	/**
	 * Attach change listeners to referenced fields so autogen
	 * re-runs when dependencies change.
	 */
	attachListeners(callback) {
		const fieldNames = this.getFieldNames();
		const searchScope = this.field.isInDeck ? this.field.deckItem : this.field.form.form;

		fieldNames.forEach(name => {
			const input = searchScope.querySelector(`[name="${name}"]`);
			if (!input) return;
			input.addEventListener("change", () => callback());
		});
	}

	/**
	 * Generate a value from the autogen pattern using current form data.
	 * Does NOT slugify - returns the raw interpolated string.
	 */
	generate() {
		let data = this.collectFormData();
		data = this.addSpecialVariables(data);

		return this.pattern.replace(/\${(.*?)}/g, (match, key) => {
			// Handle oid with zero-padding: oid-00000
			if (key.startsWith('oid-') && /^oid-0+$/.test(key)) {
				const paddingLength = key.substring(4).length;
				const oidValue = this.getCollectionCount();
				return oidValue.toString().padStart(paddingLength, '0');
			}
			// Handle uid with a custom length: uid-12 → a 12-char random id.
			// Bare ${uid} stays the default length (see addSpecialVariables).
			if (/^uid-\d+$/.test(key)) {
				return this.generateUid(parseInt(key.substring(4), 10));
			}
			return data[key] ?? "";
		});
	}

	/**
	 * Collect current form data from the appropriate scope.
	 */
	collectFormData() {
		// Read only the deck item's own inputs when in a deck; composite sub-fields
		// (e.g. an image's `name`/"Filename") must not shadow the item's own fields,
		// or patterns like ${name} would resolve to the image filename. See
		// fieldCollection.mjs.
		const raw = this.field.isInDeck
			? collectScopedInputValues(this.field.deckItem)
			: this.field.form.generateData();

		// Filter to string/number values and cap each to MAX_FIELD_LENGTH.
		return Object.entries(raw).reduce((acc, [key, value]) => {
			if (typeof value === 'string') {
				acc[key] = value.slice(0, Autogen.MAX_FIELD_LENGTH);
			} else if (typeof value === 'number') {
				acc[key] = String(value).slice(0, Autogen.MAX_FIELD_LENGTH);
			}
			return acc;
		}, {});
	}

	/**
	 * Add special autogen variables (timestamps, uuid, etc.).
	 */
	addSpecialVariables(data) {
		const now = new Date();
		data.now       = Date.now();
		data.timestamp = now.toISOString().slice(0, -5).replace(/-|:/g, '');
		data.uuid      = this.generateUuid();
		data.uid       = this.generateUid();
		data.oid       = this.getCollectionCount();

		data.currentyear  = now.getFullYear().toString();
		data.currentyear2 = now.getFullYear().toString().slice(-2);
		data.currentmonth = String(now.getMonth() + 1).padStart(2, '0');
		data.currentday   = String(now.getDate()).padStart(2, '0');

		return data;
	}

	getCollectionCount() {
		const count = this.field.form.form.getAttribute('data-collection-count');
		return count ? parseInt(count, 10) + 1 : 1;
	}

	generateUuid() {
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
			const r = Math.random() * 16 | 0;
			const v = c == 'x' ? r : (r & 0x3 | 0x8);
			return v.toString(16);
		});
	}

	/**
	 * Generate a base-36 random id of exactly `length` characters (default 7).
	 * Concatenating multiple Math.random() draws both supports lengths beyond a
	 * single draw (~11 chars) and avoids the truncation that
	 * `Math.random().toString(36).substring(2, 9)` produced when a draw had
	 * trailing zeros (occasionally yielding a shorter-than-expected id).
	 */
	generateUid(length = 7) {
		if (length <= 0) return '';
		let uid = '';
		while (uid.length < length) {
			uid += Math.random().toString(36).substring(2);
		}
		return uid.substring(0, length);
	}
}
